import React, { useState } from 'react';
import './Home.css';

function Home() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitMessage(data.message);
        setEmail('');
      } else {
        setSubmitMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Waitlist submission error:', error);
      setSubmitMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="home-page">
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <p className="hero-label">Data-driven. Horse-centered.</p>
            <h1 className="hero-title">
              See how your horse<br />
              <span className="hero-highlight">really moves</span>
            </h1>
            <p className="hero-subtitle">
              Tru-Stride delivers expert gait analysis reviews with pose tracking, 
              hoof landing insights, and actionable recommendations—so you can train, 
              manage, and care with clarity.
            </p>
            <div className="hero-actions">
              <a href="#waitlist" className="btn btn-primary btn-lg">Join the founding waitlist</a>
              <a href="#how-it-works" className="btn btn-secondary btn-lg">See a sample review</a>
            </div>
            <p className="hero-note">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s-8-4-8-10V5l8-3 8 3v7c0 6-8 10-8 10z"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
              Founding 10 spots - approval by email.
            </p>
          </div>
        </div>
      </section>

      {/* Gait Analysis Review Card */}
      <section className="gait-review-section">
        <div className="container">
          <div className="gait-review-card">
            <div className="gait-review-header">
              <span className="gait-review-label">Gait Analysis Review</span>
              <h2>Hoof Landing & Asymmetry Review</h2>
            </div>
            <div className="gait-review-body">
              <div className="gait-video-container">
                <div className="gait-video-placeholder">
                  <svg className="play-icon" width="64" height="64" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M10 8l6 4-6 4V8z" fill="currentColor"/>
                  </svg>
                  <div className="video-skeleton">
                    <svg viewBox="0 0 400 300" preserveAspectRatio="xMidYMid meet">
                      <defs>
                        <linearGradient id="skeleton-gradient">
                          <stop offset="0%" stopColor="rgba(212, 175, 55, 0.1)"/>
                          <stop offset="50%" stopColor="rgba(212, 175, 55, 0.2)"/>
                          <stop offset="100%" stopColor="rgba(212, 175, 55, 0.1)"/>
                        </linearGradient>
                      </defs>
                      <ellipse cx="200" cy="140" rx="80" ry="60" fill="url(#skeleton-gradient)" opacity="0.3"/>
                      <path d="M 120 120 Q 140 80 180 100 L 200 140 L 160 200 M 280 120 Q 260 80 220 100 L 200 140 L 240 200" stroke="var(--color-gold)" strokeWidth="2" fill="none" opacity="0.5"/>
                      <circle cx="180" cy="100" r="3" fill="var(--color-gold)"/>
                      <circle cx="220" cy="100" r="3" fill="var(--color-gold)"/>
                      <circle cx="200" cy="140" r="3" fill="var(--color-gold)"/>
                      <circle cx="160" cy="200" r="3" fill="var(--color-gold)"/>
                      <circle cx="240" cy="200" r="3" fill="var(--color-gold)"/>
                    </svg>
                  </div>
                  <div className="video-controls">
                    <button className="video-play-btn">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </button>
                    <div className="video-timeline">
                      <div className="video-progress" style={{width: '0%'}}></div>
                    </div>
                    <span className="video-time">0:04 / 0:28</span>
                    <button className="video-sound-btn">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 5L6 9H2v6h4l5 4V5z"/>
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                      </svg>
                    </button>
                    <button className="video-fullscreen-btn">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="gait-metrics-grid">
                  <div className="gait-metric">
                    <div className="gait-metric-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14"/>
                      </svg>
                    </div>
                    <div className="gait-metric-content">
                      <div className="gait-metric-label">Hoof Landing</div>
                      <div className="gait-metric-value">+1.8 cm</div>
                      <div className="gait-metric-sub">Forward</div>
                    </div>
                  </div>
                  <div className="gait-metric">
                    <div className="gait-metric-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                      </svg>
                    </div>
                    <div className="gait-metric-content">
                      <div className="gait-metric-label">Symmetry</div>
                      <div className="gait-metric-value">87%</div>
                      <div className="gait-metric-sub">L/R limb</div>
                    </div>
                  </div>
                  <div className="gait-metric">
                    <div className="gait-metric-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 12h18M3 6h18M3 18h18"/>
                      </svg>
                    </div>
                    <div className="gait-metric-content">
                      <div className="gait-metric-label">Stride Data</div>
                      <div className="gait-metric-value">128 cm</div>
                      <div className="gait-metric-sub">Stride length</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="gait-review-summary">
                <p className="gait-summary-note">Expert review included</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="container">
          <div className="section-header">
            <p className="section-label">Simple pricing. Powerful insight.</p>
            <h2>Choose your path</h2>
          </div>
          <div className="pricing-grid">
            
            {/* Gait Review Card */}
            <div className="pricing-card">
              <div className="pricing-header">
                <h3>Gait Review</h3>
                <div className="pricing-price">
                  <span className="price-amount">$99</span>
                  <span className="price-period">/mo</span>
                </div>
                <p className="pricing-desc">One horse - One review per month</p>
              </div>
              <ul className="pricing-features">
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  10 video clips (10-30 seconds each)
                </li>
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  2 reviews per month
                </li>
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  5 business day turnaround
                </li>
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Expert written analysis
                </li>
              </ul>
              <a href="#waitlist" className="btn btn-primary pricing-btn">Join waitlist</a>
              <p className="pricing-note">Cancel anytime</p>
            </div>

            {/* Add a Horse Card */}
            <div className="pricing-card pricing-card-accent">
              <div className="pricing-badge">Most popular</div>
              <div className="pricing-header">
                <h3>Add a horse</h3>
                <div className="pricing-price">
                  <span className="price-prefix">+</span>
                  <span className="price-amount">$50</span>
                  <span className="price-period">/mo</span>
                  <span className="price-suffix">each</span>
                </div>
                <p className="pricing-desc">Each additional horse - One review per month</p>
              </div>
              <ul className="pricing-features">
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Up to 3 horses total
                </li>
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Same 2 reviews per month per horse
                </li>
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Same 5 business day turnaround
                </li>
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Express +$49 available after subscribe
                </li>
              </ul>
              <a href="#waitlist" className="btn btn-primary pricing-btn">Join waitlist</a>
              <p className="pricing-note">Add after first subscription</p>
            </div>

            {/* Barn Custom Card */}
            <div className="pricing-card">
              <div className="pricing-header">
                <h3>Barn Custom</h3>
                <div className="pricing-price">
                  <span className="price-amount">Inquire</span>
                </div>
                <p className="pricing-desc">Custom volume pricing for teams & programs</p>
              </div>
              <ul className="pricing-features">
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  4+ horses
                </li>
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Flexible review schedules
                </li>
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Priority support
                </li>
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Team collaboration tools
                </li>
              </ul>
              <a href="#waitlist" className="btn btn-secondary pricing-btn">Inquire for pricing</a>
              <p className="pricing-note">Speak with us</p>
            </div>

          </div>
          <p className="pricing-disclaimer">
            Not veterinary coaching. Educational insights to support your horsemanship.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="container">
          <div className="section-header">
            <p className="section-label">How it works</p>
            <h2>From video to insight<br />in four simple steps</h2>
          </div>
          <div className="steps-grid">
            
            {/* Step 1 */}
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="2" y="3" width="20" height="14" rx="2"/>
                  <path d="M8 21h8M12 17v4"/>
                </svg>
              </div>
              <h3 className="step-title">Record</h3>
              <p className="step-desc">
                Send a clear side-view video of your horse in motion.
              </p>
            </div>

            {/* Step 2 */}
            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                </svg>
              </div>
              <h3 className="step-title">Analyze</h3>
              <p className="step-desc">
                Experts process videos with pose tracking and hoof landing data.
              </p>
            </div>

            {/* Step 3 */}
            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/>
                </svg>
              </div>
              <h3 className="step-title">Receive</h3>
              <p className="step-desc">
                Get an in-depth written review with visuals and recommendations.
              </p>
            </div>

            {/* Step 4 */}
            <div className="step-card">
              <div className="step-number">4</div>
              <div className="step-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h3 className="step-title">Apply & Improve</h3>
              <p className="step-desc">
                Use insights to train, manage, and care with confidence.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Trusted By Section (logos only if permission) */}
      <section id="about" className="trusted-section">
        <div className="container">
          <p className="trusted-label">Trusted by equine professionals</p>
          <div className="trusted-logos">
            <div className="trusted-badge">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="8.5" cy="7" r="4"/>
                <path d="M20 8v6M23 11h-6"/>
              </svg>
              <span>Certified Journeyman Farrier</span>
            </div>
            <div className="trusted-badge">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <span>Equine Sports Massage Therapist</span>
            </div>
          </div>
          <div className="trusted-orgs">
            <div className="trusted-org">USEA<br/><span>United States<br/>Eventing Association</span></div>
            <div className="trusted-org">USDF<br/><span>United States<br/>Dressage Federation</span></div>
            <div className="trusted-org">AQHA</div>
            <div className="trusted-org">FEI</div>
          </div>
        </div>
      </section>

      {/* FAQs Placeholder */}
      <section id="faqs" className="faqs-section">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="faqs-content">
            <p className="faqs-coming-soon">More details coming soon.</p>
          </div>
        </div>
      </section>

      {/* Final CTA / Waitlist Section */}
      <section id="waitlist" className="waitlist-section">
        <div className="container">
          <div className="waitlist-card">
            <h2>Be one of the first to see the unseen.</h2>
            <p>Join the founding waitlist</p>
            <form className="waitlist-form" onSubmit={handleWaitlistSubmit}>
              <input 
                type="email" 
                placeholder="Enter your email" 
                className="waitlist-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isSubmitting}
              />
              <button 
                type="submit" 
                className="btn btn-primary waitlist-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Joining...' : 'Join waitlist'}
              </button>
            </form>
            {submitMessage && (
              <p className={`waitlist-message ${submitMessage.includes('Thanks') ? 'success' : 'error'}`}>
                {submitMessage}
              </p>
            )}
            <p className="waitlist-note">
              Founding 10 - you'll get an email if a spot is open.
            </p>
          </div>
        </div>
      </section>

    </div>
  );
}

export default Home;
